import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { baseAPI } from '../../config';
import { toast } from 'react-toastify';
import userStore from '../../store/userStore';
import { CurrencyFormatComponent } from '../../utils/currencyUtils';
import DebtActivityModal from './DebtActivityModal';
import DebtActivityAddForm from './DebtActivityAddForm';
import {
  DEBT_ACTIVITY_LABELS,
  DEBT_POSITIVE_TYPES,
} from '@claimup/shared/constants/expedient';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const DebtActivityList = ({ idExpedient, divisa = 'EUR', onDebtUpdate }) => {
  const [debtActivity, setDebtActivity] = useState([]);
  const [importeDeuda, setImporteDeuda] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { user } = userStore();
  const isAdmin = user?.role === 'admin';

  const fetchDebtActivity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${baseAPI}/expedients/${idExpedient}/debt-activity`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setDebtActivity(response.data.debt_activity || []);
      const newImporteDeuda = response.data.importe_deuda || 0;
      setImporteDeuda(newImporteDeuda);
      if (onDebtUpdate) {
        onDebtUpdate(newImporteDeuda, response.data.total_debt || 0);
      }
    } catch (error) {
      console.error('Error fetching debt activity:', error);
      toast.error('Error al cargar la actividad de deuda');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idExpedient]);

  useEffect(() => {
    fetchDebtActivity();
  }, [fetchDebtActivity]);

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleDeleteClick = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm('¿Estás seguro de que deseas eliminar este movimiento?')) {
      return;
    }

    try {
      await axios.delete(
        `${baseAPI}/expedients/${idExpedient}/debt-activity/${item._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Movimiento eliminado');
      fetchDebtActivity();
    } catch (error) {
      console.error('Error deleting debt activity:', error);
      toast.error('Error al eliminar el movimiento');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleModalSave = () => {
    setModalOpen(false);
    setEditingItem(null);
    fetchDebtActivity();
  };

  const YELLOW_TYPES = ['bond', 'credit', 'cancellation'];

  const getTypeColor = type => {
    if (DEBT_POSITIVE_TYPES.includes(type)) {
      return 'bg-red-100 text-red-800';
    }
    if (YELLOW_TYPES.includes(type)) {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-green-100 text-green-800';
  };

  const getAmountColor = type => {
    if (DEBT_POSITIVE_TYPES.includes(type)) {
      return 'text-red-600';
    }
    if (YELLOW_TYPES.includes(type)) {
      return 'text-yellow-600';
    }
    return 'text-green-600';
  };

  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-lg border border-gray-200">
      <header className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800">Actividad de Deuda</h2>
            <div className="mt-1 text-sm text-gray-600">
              Saldo actual:{' '}
              <span className="font-semibold text-blue-600">
                <CurrencyFormatComponent value={importeDeuda} divisa={divisa} />
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="p-5">
        {debtActivity.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay movimientos registrados
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-auto w-full">
              <thead className="text-xs font-semibold text-gray-600 bg-gray-50 rounded-lg">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Concepto</th>
                  <th className="px-4 py-3 text-right">Importe</th>
                  {isAdmin && <th className="px-4 py-3 text-center">Acciones</th>}
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-gray-200">
                {debtActivity.map(item => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-gray-800">{formatDate(item.date)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                          item.type
                        )}`}
                      >
                        {DEBT_ACTIVITY_LABELS[item.type] || item.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-800 font-medium">{item.title}</div>
                      {item.description && (
                        <div className="text-gray-500 text-xs mt-0.5">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${getAmountColor(item.type)}`}>
                      {DEBT_POSITIVE_TYPES.includes(item.type) ? '+' : '-'}
                      <CurrencyFormatComponent value={item.amount} divisa={divisa} />
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={(e) => handleEditClick(e, item)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Editar"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteClick(e, item)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin && (
          <div className="mt-6 pt-5 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Añadir actividad</h3>
            <DebtActivityAddForm
              expedientId={idExpedient}
              onSuccess={fetchDebtActivity}
            />
          </div>
        )}
      </div>

      <DebtActivityModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        expedientId={idExpedient}
        editingItem={editingItem}
      />
    </div>
  );
};

export default DebtActivityList;
